/**
 * Seating someone who is not on the roster yet.
 *
 * A host adding a guest to a dinner table can only name them by invitee id —
 * that is the whole security story of the host claim endpoint, which is the one
 * request shape in the Groups surface carrying an invitee id in the identity
 * position and is safe for exactly one reason: it is host-authenticated. So a
 * guest with no roster row has no id, and there is nothing to send.
 *
 * The answer is to put them on the roster first. That is not a workaround for
 * the id requirement — they are attending the trip, so they belong on the list
 * of people attending the trip.
 *
 * The delicate part is not the insert, it is NOT MERGING. Studio must never run
 * its own matching rule: the API has one, its preview and its write share it,
 * and a second rule in the client that disagrees is how a 137-person roster
 * became 274. So the flow is preview -> read the server's verdict -> write only
 * what the server already said it would do, carrying the fingerprint that makes
 * the write resolve on approved ids alone. Everything in this file is a reader
 * of that verdict.
 */
import type { InviteeImportPreview, InviteeImportResult } from './inviteeImport';

/**
 * What the server says this typed-in guest is.
 *
 * `needs_review` is the important arm: it is the server saying "this might be
 * somebody you already have, and I am not sure". Studio stops there instead of
 * guessing, because guessing wrong here merges two people — one guest's invite
 * goes to the other's address and the overwritten one leaves no trace.
 */
export type DinnerGuestPlan =
  | { kind: 'existing'; inviteeId: string; fullName: string }
  | { kind: 'create' }
  | { kind: 'needs_review'; message: string };

function guestNames(preview: InviteeImportPreview): string {
  const row = preview.rows[0];
  const names = [
    row?.matchedGuest?.fullName,
    ...(row?.candidates ?? []).map((candidate) => candidate.fullName),
  ].filter((name): name is string => Boolean(name && name.trim()));
  return Array.from(new Set(names)).join(', ');
}

/**
 * Read a one-row import preview.
 *
 * Only a match the server itself calls certain (`update`, an id, and
 * `requiresConfirmation` false — i.e. matched on an id or an email address, not
 * on a name) is treated as an existing guest. Anything the server flags as a
 * guess comes back as `needs_review` and is handed to the host as words.
 */
export function dinnerGuestPlan(preview: InviteeImportPreview): DinnerGuestPlan {
  const rows = preview?.rows ?? [];
  if (rows.length !== 1) {
    return {
      kind: 'needs_review',
      message:
        `Studio asked the API about one guest and got ${rows.length} rows back. Nothing was` +
        ' written. Add them on the Guests tab instead.',
    };
  }

  const row = rows[0];
  if (row.action === 'update' && row.matchedInviteeId && !row.requiresConfirmation) {
    return {
      kind: 'existing',
      inviteeId: row.matchedInviteeId,
      fullName: row.matchedGuest?.fullName || row.fullName,
    };
  }

  if (row.action === 'create') return { kind: 'create' };

  const names = guestNames(preview);
  return {
    kind: 'needs_review',
    message:
      (names
        ? `That could be ${names}, already on this trip.`
        : 'The API is not sure whether that guest is already on this trip.') +
      (row.reason ? ` ${row.reason}` : '') +
      ' Nobody was added and no seat was saved — search the roster above and pick the right' +
      ' person, or add them on the Guests tab first.',
  };
}

/**
 * The id of the guest an import just created, or null when that cannot be
 * established beyond doubt.
 *
 * Identity diff, never a name match: the import answers with the whole roster
 * as it now stands, so the row that was not there before is the row it made.
 * Refuses on anything else — if the roster moved underneath us (someone else
 * imported at the same time, so two ids are new) the honest answer is "I do not
 * know which one is yours", and a guess here saves a stranger's seat.
 */
export function createdInviteeId(
  before: readonly { id: string }[],
  result: InviteeImportResult,
): string | null {
  if (!result || result.createdCount !== 1 || (result.errors?.length ?? 0) > 0) return null;

  const known = new Set(before.map((invitee) => invitee.id));
  const fresh = (result.invitees ?? [])
    .map((invitee) => (invitee as { id?: unknown })?.id)
    .filter((id): id is string => typeof id === 'string' && id.length > 0)
    .filter((id) => !known.has(id));

  return fresh.length === 1 ? fresh[0] : null;
}

/**
 * What to tell a host whose guest went onto the roster but not onto the table.
 *
 * Two separate facts, and the message has to carry both or the host re-types the
 * name and creates a duplicate: the guest DOES exist now, and the seat does NOT.
 */
export function unseatedGuestNote(fullName: string, result: InviteeImportResult): string {
  const who = fullName.trim() || 'That guest';
  const rowError = result?.errors?.[0]?.error;
  if (rowError) return `${who} was not added: ${rowError}`;

  if (result?.createdCount === 0 && result?.updatedCount > 0) {
    return (
      `${who} was already on this trip, so nothing new was created — and Studio could not tell` +
      ' which roster row is theirs, so no seat was saved. Search for them above and add them.'
    );
  }

  return (
    `${who} is on the trip now, but Studio could not tell which roster row is theirs, so no seat` +
    ' was saved. Search for them above and add them — do not re-type the name, that makes a' +
    ' second copy of them.'
  );
}

/**
 * What the picker says when it has nobody to offer.
 *
 * Same three-way split as the room-occupant picker, for the same reason: an
 * empty list is produced by "nobody matches", "the roster is empty" and
 * "everyone here is already seated", and only the first is about the query.
 * A host told "everyone is already at this table" on a trip with no guests has
 * been told something false and pointed away from the fix.
 */
export function dinnerPickerEmptyNote(
  roster: readonly unknown[],
  seatedCount: number,
  query: string,
): string {
  const typed = query.trim();
  if (typed) return `Nobody on this trip matches “${typed}”.`;
  if (roster.length === 0) {
    return 'No guests on this trip yet — import the roster on the Guests tab, or add someone below.';
  }
  if (seatedCount > 0) return 'Everyone on the roster already has a seat at this table.';
  return 'No guests to show.';
}
